import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import useMediaStore from '../stores/media';
import { Track } from '../types';
import useRenditionStore from '../stores/rendition';
import useSettingStore from '../stores/setting';
import { findCorrectNextTrack, findCorrectPrevTrack } from '../util/navigation-controls-helper';
import useNavigationStore from '../stores/navigation';
import useAudioTracks from '../composables/use-audio-tracks';

export const SKIP_BY_DEFAULT = 'default';
export const SKIP_BY_HEADINGS = 'headings';
export const SKIP_BY_PAGES = 'pages';
export const SKIP_BY_TIME = 'time';

type NavigationDirection = 'forward' | 'backward';

const useNavigationControls = () => {
  const { flattenedToc, pages, currentPageIndex } = storeToRefs(useNavigationStore());
  const { getActiveTocHrefByHref } = useNavigationStore();

  const mediaStore = useMediaStore();
  const { setCurrentTrack, getPrevTrack, getNextTrack, getTracksByHref, setIsUserChangingTrack } = mediaStore;
  const { getNextTrackOnPage, getPrevTrackOnPage, currentTrack, currentPlayerTime } = storeToRefs(mediaStore);

  const { goToPage } = useRenditionStore();
  const { skipBy } = storeToRefs(useSettingStore());

  const { loadNeighborSpineTracks } = useAudioTracks();

  /**
   * Parses level from string ("headings_1" -> 0)
   */
  const skipByLevel = computed<number>(() => {
    if (skipBy.value?.includes('_')) {
      return parseInt(skipBy.value.split('_')[1], 10) - 1;
    }
    return -1;
  });

  /**
   * Logic for Headings and Pages navigation
   */
  const navigateThroughHeadingsAndPages = (direction: NavigationDirection): Track | string => {
    const current = currentTrack.value;
    const [mode] = skipBy.value?.split('_') || [''];

    let hrefList: string[] = [];

    // Build the relevant list of HREFs to jump between
    if (mode === SKIP_BY_PAGES) {
      hrefList = pages.value.map((p) => p.href);
    } else if (mode === SKIP_BY_HEADINGS) {
      hrefList = flattenedToc.value
        .filter((item) => {
          const itemLevel = item.level ?? 0;
          return itemLevel <= skipByLevel.value;
        })
        .map((item) => item.href);
    } else {
      return current || '';
    }

    let currentHref = '';
    let currentIndex = -1;

    // For page navigation, use the currentPageIndex directly (most reliable)
    if (mode === SKIP_BY_PAGES && currentPageIndex.value !== undefined) {
      currentIndex = currentPageIndex.value;
      if (currentIndex >= 0 && currentIndex < hrefList.length) {
        currentHref = hrefList[currentIndex];
      }
    }
    // For headings, use track href
    else if (current && current.text.src) {
      currentHref = current.text.src;
      // Strip fragment identifier for matching
      const currentHrefBase = currentHref.split('#')[0];
      currentIndex = hrefList.findIndex((href) => href.split('#')[0] === currentHrefBase);
    }

    // If we are between headings, use the dependency map to find the "active" parent heading
    if (currentIndex === -1 && mode === SKIP_BY_HEADINGS && currentHref) {
      const activeHeadingHref = getActiveTocHrefByHref(currentHref);
      currentIndex = activeHeadingHref ? hrefList.indexOf(activeHeadingHref) : -1;
    }

    const offset = direction === 'forward' ? 1 : -1;
    const targetIndex = currentIndex + offset;

    if (targetIndex < 0 || targetIndex >= hrefList.length) {
      return current || '';
    }

    const targetHref = hrefList[targetIndex];

    // Try to get media track for target, but navigate even if none exists
    const tracks = getTracksByHref(targetHref);

    // Return track if available, otherwise return just the href for navigation
    if (tracks.length > 0) {
      return tracks[0];
    }

    // For page navigation, use the page's CFI if available (more precise than href)
    if (mode === SKIP_BY_PAGES && pages.value[targetIndex]?.cfi) {
      return pages.value[targetIndex].cfi || '';
    }

    // No track found, but we still want to navigate to this href
    return targetHref;
  };

  /**
   * Logic for Time-based skipping (15s / 30s)
   */
  const skipByTime = (direction: NavigationDirection): Track => {
    const current = currentTrack.value;
    if (!current) throw new Error('Current track is not available.');

    // In time mode, skipByLevel contains the seconds
    const secondsToSkip = skipByLevel.value + 1;

    try {
      const nextTrackGetter = direction === 'forward' ? getNextTrack : getPrevTrack;
      return direction === 'forward'
        ? findCorrectNextTrack(secondsToSkip, current, currentPlayerTime.value, nextTrackGetter)
        : findCorrectPrevTrack(secondsToSkip, current, currentPlayerTime.value, nextTrackGetter);
    } catch (e) {
      console.error('Time skip failed:', e);
      return current;
    }
  };

  const navigateThroughTracks = async (direction: NavigationDirection): Promise<Track | null> => {
    const nextTrack = direction === 'forward' ? getNextTrackOnPage.value : getPrevTrackOnPage.value;

    if (nextTrack) {
      return nextTrack;
    }

    //In the case of being the last track of the current spine we need to load more
    const currentHref = currentTrack.value?.text?.src.split('#')[0];
    if (!currentHref) return null;

    //Load more tracks
    const loadedTracks = await loadNeighborSpineTracks(currentHref, direction === 'forward' ? 'next' : 'prev');

    if (loadedTracks?.tracks.length) {
      // Return first track from next spine (forward) or last track from prev spine (backward)
      return direction === 'forward' ? loadedTracks.tracks[0] : loadedTracks.tracks[loadedTracks.tracks.length - 1];
    }

    return null;
  };

  const handlerFunction = computed(() => {
    if (!skipBy.value) return navigateThroughTracks;

    const [mode] = skipBy.value.split('_');
    switch (mode) {
      case SKIP_BY_HEADINGS:
      case SKIP_BY_PAGES:
        return navigateThroughHeadingsAndPages;
      case SKIP_BY_TIME:
        return skipByTime;
      default:
        return navigateThroughTracks;
    }
  });

  const performNavigation = async (direction: NavigationDirection) => {
    if (!handlerFunction.value) return;

    const result = await handlerFunction.value(direction);

    if (!result) return;

    // Handle both Track objects and string hrefs/CFIs
    let target: string;
    let trackToSet: Track | undefined;

    if (typeof result === 'string') {
      // Result is an href or CFI
      target = result;
      trackToSet = undefined;
    } else {
      // Result is a Track object
      const track = result as Track;
      target = track.text.src;
      trackToSet = track;
    }

    goToPage({
      target,
      isGoingForwardsInBook: direction === 'forward',
    });

    if (trackToSet) {
      setCurrentTrack(trackToSet);
      setIsUserChangingTrack(true);
    }
  };

  return {
    navigateForward: () => performNavigation('forward'),
    navigateBack: () => performNavigation('backward'),
  };
};

export default useNavigationControls;
